/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-pricing-bands.ts
 * FILE_ID: QB-AGGRID-BRAVADA-PRICING-001
 * PURPOSE: The pricing-section bands of the Bravada Excel "Scope &
 *          Pricing" master template — PRODUCTS (with aggregate
 *          columns), PLANT RENTAL, and SUBCONTRACTOR. Split out of
 *          bravada-column-bands.ts so each band schema file stays
 *          under the 300-line cap.
 *
 * EXCEL EVIDENCE (Q9525R9 master template, header rows 7-15):
 *   Excel col AM "TOTAL COST $'s"            → material_cost
 *   Excel col AN "Wastage Allowance %"        → material_wastage_percent (NEW)
 *   Excel col AO "Per M2"                     → material_cost_per_m2 (NEW)
 *   Excel col AP "Total + Wastage $'s"        → material_total_with_wastage (NEW)
 *   Excel col AQ "Alternative Markup %"       → material_markup_percent (NEW)
 *   Excel col AR "Product Mark Up $'s"        → material_markup_amount (NEW)
 *   Excel col AS "Product Charge Out"         → material_chargeout
 *   Excel col AT "Product Charge Out Per M2"  → material_charge_out_per_m2 (NEW)
 *
 *   Excel col AU "Description"                → plant_description
 *   Excel col AV "Units"                      → plant_quantity
 *   Excel col AW "Unit Cost"                  → plant_unit_cost
 *   Excel col AX "Total Cost"                 → plant_cost
 *   Excel col AY "Mark Up %"                  → plant_markup_percent (NEW)
 *   Excel col AZ "Mark Up $'s"                → plant_markup_amount (NEW)
 *   Excel col BA "Charge Out $'s"             → plant_chargeout
 *
 *   Excel col BB "Description"                → subcon_description
 *   Excel col BC "Units"                      → subcon_days
 *   Excel col BD "Unit Cost"                  → subcon_quote
 *   Excel col BE "Total Cost"                 → subcon_cost
 *   Excel col BF "Mark Up %"                  → subcon_markup_percent (NEW)
 *   Excel col BG "Mark Up $'s"                → subcon_markup_amount (NEW)
 *   Excel col BH "Charge Out $'s"             → subcon_chargeout
 *
 * COMPUTED COLUMN RESOLUTION:
 *   material_markup_amount  = material_chargeout - material_cost
 *   material_markup_percent = (material_chargeout - material_cost) /
 *                             material_cost * 100
 *   material_cost_per_m2    = material_cost / quantity
 *   material_charge_out_per_m2 = material_chargeout / quantity
 *   material_wastage_percent   = override_material_wastage (null → null)
 *   material_total_with_wastage = material_cost × (1 + wastage)
 *
 *   plant/subcon markup_amount + markup_percent follow the same
 *   pattern (chargeout - cost, divided by cost).
 *
 * Why these are computed (not stored fields):
 *   The legacy model stores only cost + chargeout per discipline.
 *   The markup % and $ are derivatives — making them computed lets
 *   the user edit cost OR chargeout OR the markup % override and
 *   keep the row consistent. Persisting a derived field would
 *   duplicate truth and risk drift.
 *
 * RELATED:
 *   - QB-AGGRID-BRAVADA-BANDS-001 (bravada-column-bands.ts) — assembles
 *     these into the full BRAVADA_COLUMN_BANDS array.
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — implements the
 *     computed-value resolution for the NEW ids declared here.
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — renders the
 *     bands into AG-Grid ColGroupDefs.
 */

import type { ColumnBand } from '../scope-sheet-grid/ScopeSheetGridTypes'
import { numCol, textCol, WIDTH } from './bravada-band-helpers'
import { BRAVADA_PRODUCT_SLOT_SUBBANDS } from './bravada-product-slot-bands'

// ---------------------------------------------------------------------------
// PRODUCTS band — per-unit inputs + Excel aggregate columns
// ---------------------------------------------------------------------------
// The PRODUCTS band represents Excel's monolithic horizontal pricing
// strip. Per-unit columns (Unit cost, m², LM/unit) come first because
// they're the inputs estimators type FIRST when pricing a new line.
// Aggregate columns (Total Cost, Wastage, Markup, Charge Out) follow
// in left-to-right Excel order so the page reads naturally.

export const BRAVADA_PRODUCTS_BAND: ColumnBand = {
  id: 'products',
  label: 'PRODUCTS',
  color: 'amber',
  bgClass: 'bg-amber-50/50 dark:bg-amber-950/30',
  textClass: 'text-amber-600 dark:text-amber-400',
  // Nested layout: 4 slot sub-bands (Primer/Basecoat/Topcoat/Accessories,
  // Excel cols AB-AL) render BEFORE the aggregate columns below.
  // AG-Grid renders subBands as inner ColGroupDefs nested under the
  // parent "PRODUCTS" header — the same hierarchy estimators see in
  // the .xlsm. Values for each sub-band column come from
  // QuoteLineItem.materials[slot=<slot>] via getSlotFieldValue (see
  // bravada-material-slots.ts).
  subBands: BRAVADA_PRODUCT_SLOT_SUBBANDS,
  columns: [
    // Excel col AM "TOTAL COST $'s" — aggregate cost across all
    // product types. Same persisted field as the Primer Unit cost
    // sub-band; the column label matches Excel where the same value
    // is shown both per-slot AND as the band total.
    numCol({
      id: 'material_total_cost',
      label: 'TOTAL COST $',
      width: WIDTH.totalCost,
      computed: true,
    }),
    // Excel col AN "Wastage Allowance %" — override on the row. When
    // null, falls back to quote.config.materialWastage at the engine
    // level. Display column shows the per-line override only (the
    // global default lives on the Assumptions sheet).
    numCol({
      id: 'material_wastage_percent',
      label: 'Wastage %',
      width: WIDTH.wastagePercent,
      computed: true,
      override: true,
    }),
    // Excel col AO "Per M2" — material cost per m².
    numCol({
      id: 'material_cost_per_m2',
      label: 'Cost / m²',
      width: WIDTH.perM2,
      computed: true,
    }),
    // Excel col AP "Total + Wastage $'s" — cost grossed up by wastage.
    numCol({
      id: 'material_total_with_wastage',
      label: 'Total + Wastage',
      width: WIDTH.withWastage,
      computed: true,
    }),
    // Excel col AQ "Alternative Markup %" — override on the row.
    numCol({
      id: 'material_markup_percent',
      label: 'Markup %',
      width: WIDTH.markupPercent,
      computed: true,
      override: true,
    }),
    // Excel col AR "Product Mark Up $'s" — derived markup dollars.
    numCol({
      id: 'material_markup_amount',
      label: 'Markup $',
      width: WIDTH.markupAmount,
      computed: true,
    }),
    // Excel col AS "Product Charge Out" — chargeout column (same
    // persisted field, different label / position from the per-unit
    // placeholder above).
    numCol({
      id: 'material_charge_out',
      label: 'Charge Out',
      width: WIDTH.chargeOut,
      computed: true,
    }),
    // Excel col AT "Product Charge Out Per M2".
    numCol({
      id: 'material_charge_out_per_m2',
      label: 'C/O / m²',
      width: WIDTH.chargeOutPerM2,
      computed: true,
    }),
  ],
}

// ---------------------------------------------------------------------------
// PLANT RENTAL band — Excel cols AU..BA
// ---------------------------------------------------------------------------
// Estimators enter plant gear (scissor lifts, pumps, dewatering rigs)
// against individual lines or — more commonly — against an Other
// Charges section line. We expose the columns at line level so the
// Excel users have parity; they typically leave them blank on scope
// lines and roll them up in Other Charges.

export const BRAVADA_PLANT_BAND: ColumnBand = {
  id: 'plant',
  label: 'PLANT RENTAL',
  color: 'lime',
  bgClass: 'bg-lime-50/50 dark:bg-lime-950/30',
  textClass: 'text-lime-700 dark:text-lime-400',
  columns: [
    textCol({
      id: 'plant_description',
      label: 'Description',
      width: WIDTH.rentDescription,
    }),
    numCol({
      id: 'plant_quantity',
      label: 'Units',
      width: WIDTH.rentUnits,
    }),
    numCol({
      id: 'plant_unit_cost',
      label: 'Unit Cost',
      width: WIDTH.rentUnitCost,
    }),
    numCol({
      id: 'plant_cost',
      label: 'Total Cost',
      width: WIDTH.rentTotalCost,
    }),
    // Computed: (chargeout - cost) / cost * 100. Override-able via
    // QuoteLineItem.override_plant_markup at the engine level.
    numCol({
      id: 'plant_markup_percent',
      label: 'Markup %',
      width: WIDTH.rentMarkupPercent,
      computed: true,
      override: true,
    }),
    numCol({
      id: 'plant_markup_amount',
      label: 'Markup $',
      width: WIDTH.rentMarkupAmount,
      computed: true,
    }),
    numCol({
      id: 'plant_chargeout',
      label: 'Charge Out',
      width: WIDTH.rentChargeOut,
    }),
  ],
}

// ---------------------------------------------------------------------------
// SUBCONTRACTOR band — Excel cols BB..BH
// ---------------------------------------------------------------------------

export const BRAVADA_SUBCON_BAND: ColumnBand = {
  id: 'subcon',
  label: 'SUBCONTRACTOR',
  color: 'rose',
  bgClass: 'bg-rose-50/50 dark:bg-rose-950/30',
  textClass: 'text-rose-700 dark:text-rose-400',
  columns: [
    textCol({
      id: 'subcon_description',
      label: 'Description',
      width: WIDTH.rentDescription,
    }),
    // Subcontractor quotes in Bravada are priced by Days/units; the
    // model exposes `subcon_days` for this column.
    numCol({
      id: 'subcon_days',
      label: 'Units',
      width: WIDTH.rentUnits,
    }),
    // `subcon_quote` is the per-unit (or per-quote) rate the supplier
    // gave the estimator. Excel calls this column "Unit Cost".
    numCol({
      id: 'subcon_quote',
      label: 'Unit Cost',
      width: WIDTH.rentUnitCost,
    }),
    numCol({
      id: 'subcon_cost',
      label: 'Total Cost',
      width: WIDTH.rentTotalCost,
    }),
    numCol({
      id: 'subcon_markup_percent',
      label: 'Markup %',
      width: WIDTH.rentMarkupPercent,
      computed: true,
      override: true,
    }),
    numCol({
      id: 'subcon_markup_amount',
      label: 'Markup $',
      width: WIDTH.rentMarkupAmount,
      computed: true,
    }),
    numCol({
      id: 'subcon_chargeout',
      label: 'Charge Out',
      width: WIDTH.rentChargeOut,
    }),
  ],
}

/**
 * Pricing bands assembled in Excel left-to-right order:
 * PRODUCTS → PLANT RENTAL → SUBCONTRACTOR.
 */
export const BRAVADA_PRICING_BANDS: ColumnBand[] = [
  BRAVADA_PRODUCTS_BAND,
  BRAVADA_PLANT_BAND,
  BRAVADA_SUBCON_BAND,
]
